// Generic card finishes that work across all trading card games and sports cards.
// Tight, universal 5-option set — legacy/game-specific values collapse via FINISH_MAPPING.

export type CardFinish =
  | 'normal'
  | 'holofoil'
  | 'reverse_holo'
  | 'foil'
  | 'textured';

export const FINISH_LABELS: Record<CardFinish, string> = {
  normal: 'Normal',
  holofoil: 'Holo',
  reverse_holo: 'Reverse Holo',
  foil: 'Foil',
  textured: 'Textured',
};

export const FINISH_OPTIONS: { value: CardFinish; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'holofoil', label: 'Holo' },
  { value: 'reverse_holo', label: 'Reverse Holo' },
  { value: 'foil', label: 'Foil' },
  { value: 'textured', label: 'Textured' },
];

// Map legacy/game-specific finishes to consolidated values
export const FINISH_MAPPING: Record<string, CardFinish> = {
  // Normal / base
  normal: 'normal',
  non_holo: 'normal',
  nonfoil: 'normal',
  base: 'normal',

  // Holo (full holofoil, sports refractor/prizm patterns)
  holofoil: 'holofoil',
  holo: 'holofoil',
  cosmos_holo: 'holofoil',
  cracked_ice_holo: 'holofoil',
  galaxy_holo: 'holofoil',
  master_ball: 'holofoil',
  prism: 'holofoil',
  refractor: 'holofoil',
  prizm: 'holofoil',
  silver_prizm: 'holofoil',
  gold_prizm: 'holofoil',
  rainbow_prizm: 'holofoil',
  chrome: 'holofoil',

  // Reverse holo
  reverse_holo: 'reverse_holo',
  reverse_holofoil: 'reverse_holo',
  reverse: 'reverse_holo',

  // Foil (generic premium foil — MTG, etched, stamped)
  foil: 'foil',
  cold_foil: 'foil',
  etched: 'foil',
  etched_foil: 'foil',
  gold: 'foil',
  silver: 'foil',
  stamped: 'foil',
  rainbow_foil: 'foil',
  oil_slick: 'foil',

  // Textured (embossed, alt-art, sports patch/relic)
  textured: 'textured',
  full_art: 'textured',
  alt_art: 'textured',
  embossed: 'textured',
  patch: 'textured',
  relic: 'textured',
  enchanted: 'textured',
};

export function normalizeFinish(finish: string | null | undefined): CardFinish | null {
  if (!finish) return null;
  const lower = finish.toLowerCase().replace(/\s+/g, '_');
  return FINISH_MAPPING[lower] || null;
}

/**
 * Display label for an item's finish. Prefers the seller's free-text
 * `finish_label` (e.g. "Foil Etched"); falls back to the canonical bucket label.
 */
export function getDisplayFinish(item: { finish?: string | null; finish_label?: string | null }): string | null {
  if (item.finish_label && item.finish_label.trim()) return item.finish_label.trim();
  if (item.finish && (FINISH_LABELS as Record<string, string>)[item.finish]) {
    return (FINISH_LABELS as Record<string, string>)[item.finish];
  }
  return null;
}
